import {
  useWSSnackbar,
  WSButton,
  WSButtons,
  WSElement,
  WSFlexBox,
  WSForm,
  WSIconList,
  WSText
} from "@wingspanhq/fe-component-library";
import React, { useState } from "react";
import cn from "classnames";
import { CSVLink } from "react-csv";
import { SetupFlowRouteComponentProps } from "../../components/SetupFlow/SetupFlow";
import { NEC1099FilingContainer } from "../components/NEC1099FilingContainer";

import styles from "./styles.module.scss";
import { useCollaboratorsQuery } from "../../query/payments/queries";
import { WSQueries } from "../../query/WSQuery";
import {
  BatchProcessingStrategy,
  ICollaboratorSchema
} from "@wingspanhq/payments/dist/interfaces";
import { getVisibleCollaborators } from "../../query/payments/selectors";
import { useBulkUploadPayablesImporter } from "../../Invoices/screens/payables/bulkUploadPayables/bulkUploadPayablesImporter";
import {
  PayableStatus,
  UploadPayable
} from "../../Invoices/screens/payables/bulkUploadPayables/BulkUploadPayablesInfo";
import { useHistory } from "react-router-dom";
import { useCreateBulkPayableBatch } from "../../query/payments/mutations";
import { WSErrorMessage } from "../../components/WSErrorMessage/WSErrorMessage";

export const NEC1099FilingOffPlatformPayments: React.FC<SetupFlowRouteComponentProps> = ({
  onBack,
  onNext
}) => {
  const [isFlatfileLoading, setIsFlatfileLoading] = useState(false);
  const { openSnackbar } = useWSSnackbar();
  const history = useHistory();

  const collaboratorsQuery = useCollaboratorsQuery();
  const [
    createBulkPayableBatch,
    createBulkPayableBatchMeta
  ] = useCreateBulkPayableBatch();

  const bulkUploadPayablesImporter = useBulkUploadPayablesImporter({
    payableStatus: PayableStatus.Paid,
    onClose: () => {
      setIsFlatfileLoading(false);
    }
  });

  const importData = async () => {
    const results = await bulkUploadPayablesImporter();
    const lineItems = results.validData as Array<UploadPayable>;
    if (lineItems.length === 0) {
      openSnackbar({
        duration: 5000,
        type: "warning",
        message: "Sorry, Looks like the uploaded spreadsheet is empty!"
      });
      return;
    }
    console.log("Line Items");
    console.log(JSON.stringify(lineItems));
    await createBulkPayableBatch(
      {
        processingStrategy: BatchProcessingStrategy.Single
      },
      {
        onSuccess: response => {
          setIsFlatfileLoading(false);
          onNext({
            lineItems: lineItems,
            fileName: results.fileName,
            bulkPayableBatchId: response.bulkPayableBatchId,
            /**
             * The uuid of the batch assigned by Flatfile
             */
            flatFileBatchId: results.batchId,
            search: `?status=${PayableStatus.Paid}`
          });
        }
      }
    );
  };

  const onUpload = async () => {
    setIsFlatfileLoading(true);
    await importData();
  };

  const populateCollaboratorsCsv = (collaborators: ICollaboratorSchema[]) => {
    let activeCollaborators = getVisibleCollaborators(collaborators);
    return activeCollaborators.map(collaborator => {
      return {
        collaboratorId: collaborator.collaboratorId,
        contractorId: collaborator.labels?.contractorId || "",
        email: collaborator.member.user.email || "",
        lineItemTitle: "Payments made off of Wingspan for 2022",
        amount: 0,
        paidDate: "2022-01-01",
        reimbursable: false
      };
    });
  };

  return (
    <NEC1099FilingContainer title="Off-platform payments">
      <WSQueries queries={{ collaboratorsQuery }}>
        {({ collaboratorsQuery: { data: collaborators } }) => {
          const downloadTemplateCsvProps = {
            filename: `Wingspan_Off_Platform_Payments_Template.csv`,
            headers: [
              { label: "Collaborator ID", key: "collaboratorId" },
              { label: "External Collaborator ID", key: "contractorId" },
              { label: "Email", key: "email" },
              { label: "Line Item Title", key: "lineItemTitle" },
              { label: "Amount", key: "amount" },
              { label: "Pay Date", key: "paidDate" },
              { label: "Reimbursable", key: "reimbursable" }
            ],
            data: populateCollaboratorsCsv(collaborators)
          };
          return (
            <>
              <WSText.Heading2 mb="L">
                Import payments made outside Wingspan
              </WSText.Heading2>

              <WSText as="ol" mb="S" className={styles.bulletedList}>
                <WSText.LabelBold as="li">
                  1. Invite everyone to Wingspan
                  <WSText.Paragraph color="grey700">
                    Ensure you have already invited all your collaborators who
                    will receive a 1099 to sign up for Wingspan.
                  </WSText.Paragraph>
                </WSText.LabelBold>

                <WSText.LabelBold as="li">
                  2. Upload a spreadsheet of payments to collaborators
                  <WSText.Paragraph color="grey700">
                    Please make sure your spreadsheet contains the following
                    columns:
                  </WSText.Paragraph>
                </WSText.LabelBold>

                <WSFlexBox mt="L" mb="M">
                  <WSElement mr="S" style={{ flex: 1 }}>
                    <WSText.Heading4 mb="S">Required</WSText.Heading4>
                    <WSIconList.Checks
                      items={[
                        "Email",
                        "Collaborator ID or External ID",
                        "Amount (USD)",
                        "Line Item Title",
                        "Pay Date"
                      ]}
                      mb="M"
                    />
                  </WSElement>
                  <WSElement style={{ flex: 1 }}>
                    <WSText.Heading4 mb="S">Optional</WSText.Heading4>
                    <WSIconList.Checks
                      items={[
                        "Due Date",
                        "Line Item Description",
                        "Invoice Notes",
                        "Reimbursables"
                      ]}
                      mb="M"
                    />
                  </WSElement>
                </WSFlexBox>

                <WSText.Paragraph color="grey700" mb="S">
                  You can download a template containing all of your
                  collaborators below.
                </WSText.Paragraph>

                <WSText.LabelBold as="li">
                  3. Import and review line items
                  <WSText.Paragraph color="grey700" mb="S">
                    Review your imported line items to ensure their information
                    is correct. You can either upload a spreadsheet that
                    includes:
                  </WSText.Paragraph>
                  <WSText.Label mb="S" color="grey700">
                    <WSText.LabelBold inline>A. </WSText.LabelBold>
                    <WSText.Paragraph color="grey700" inline mb="S">
                      One row for each collaborator representing the sum of all
                      payments made outside Wingspan or
                    </WSText.Paragraph>
                  </WSText.Label>
                  <WSText.Label mb="S" color="grey700">
                    <WSText.LabelBold inline>B. </WSText.LabelBold>
                    <WSText.Paragraph color="grey700" inline>
                      Multiple rows for each collaborator representing itemized
                      line items
                    </WSText.Paragraph>
                  </WSText.Label>
                </WSText.LabelBold>
                <WSText.Paragraph color="grey700">
                  These imported payments will appear as payables marked
                  "paid-off-platform" and will be visible to your collaborators.
                </WSText.Paragraph>
              </WSText>

              <WSErrorMessage
                contextKey="BulkPayableBatchCreate"
                error={createBulkPayableBatchMeta.error}
              />

              <WSButtons mt="XL">
                <CSVLink {...downloadTemplateCsvProps}>
                  <WSButton.Secondary
                    className={cn(styles.actionBtn, styles.downloadBtn)}
                    fullWidth
                    icon="download"
                    p="S"
                  >
                    Download template
                  </WSButton.Secondary>
                </CSVLink>

                <WSButton.Primary
                  className={cn(styles.actionBtn, styles.uploadBtn)}
                  fullWidth
                  icon="upload"
                  p="S"
                  data-testid="importPayments"
                  onClick={onUpload}
                  loading={
                    isFlatfileLoading || createBulkPayableBatchMeta.isLoading
                  }
                >
                  Import payments
                </WSButton.Primary>
              </WSButtons>
            </>
          );
        }}
      </WSQueries>
    </NEC1099FilingContainer>
  );
};
